import {
  CLARITY_DISABLE_SESSION_KEY,
  CLARITY_PROJECT_ID,
  clearClarityCookies,
  isClaritySensitivePath,
} from "./clarity";

export const ANALYTICS_CONSENT_KEY = "lukkey_analytics_consent_v1";
export const ANALYTICS_CONSENT_PENDING = "pending";
export const ANALYTICS_CONSENT_GRANTED = "granted";
export const ANALYTICS_CONSENT_DENIED = "denied";

const GOOGLE_MEASUREMENT_ID = "G-4B5F88G9L9";
const META_PIXEL_ID = "1985700702070135";
const CLARITY_SCRIPT_ID = "lukkey-clarity-script";
const META_PIXEL_SCRIPT_ID = "lukkey-meta-pixel-script";

export const isTrackingHostAllowed = () => {
  if (typeof window === "undefined") return false;
  if (window.__lukkeyDisableClarity) return false;
  const host = String(window.location?.hostname || "");
  return host !== "localhost" && host !== "127.0.0.1";
};

const getStoredConsent = () => {
  if (typeof window === "undefined") return ANALYTICS_CONSENT_GRANTED;
  try {
    const stored = window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
    if (stored === ANALYTICS_CONSENT_GRANTED || stored === ANALYTICS_CONSENT_DENIED) {
      return stored;
    }
  } catch {}
  return ANALYTICS_CONSENT_GRANTED;
};

const setClarityDisabled = (disabled) => {
  if (typeof window === "undefined") return;
  try {
    if (disabled) {
      window.sessionStorage.setItem(CLARITY_DISABLE_SESSION_KEY, "1");
      return;
    }
    window.sessionStorage.removeItem(CLARITY_DISABLE_SESSION_KEY);
  } catch {}
};

const buildGoogleConsentPayload = (state) => {
  const granted = state === ANALYTICS_CONSENT_GRANTED;
  return {
    analytics_storage: granted ? "granted" : "denied",
    ad_storage: granted ? "granted" : "denied",
    ad_user_data: granted ? "granted" : "denied",
    ad_personalization: granted ? "granted" : "denied",
  };
};

const updateGoogleConsent = (state) => {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  window.gtag("consent", "update", buildGoogleConsentPayload(state));
};

const ensureClarityQueue = () => {
  if (typeof window === "undefined") return null;
  window.clarity =
    window.clarity ||
    function clarityProxy() {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };
  return window.clarity;
};

const ensureMetaPixelQueue = () => {
  if (typeof window === "undefined") return null;
  if (typeof window.fbq === "function") return window.fbq;

  const fbq = function fbqProxy() {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, arguments);
      return;
    }
    fbq.queue.push(arguments);
  };

  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  window.fbq = fbq;
  window._fbq = fbq;
  return fbq;
};

const updateMetaPixelConsent = (state) => {
  if (typeof window === "undefined") return;
  if (typeof window.fbq !== "function") return;
  window.fbq("consent", state === ANALYTICS_CONSENT_GRANTED ? "grant" : "revoke");
};

export const getAnalyticsConsentState = () => getStoredConsent();

export const persistAnalyticsConsentState = (state) => {
  if (typeof window === "undefined") return;
  const nextState =
    state === ANALYTICS_CONSENT_GRANTED || state === ANALYTICS_CONSENT_DENIED
      ? state
      : ANALYTICS_CONSENT_PENDING;

  try {
    if (nextState === ANALYTICS_CONSENT_PENDING) {
      window.localStorage.removeItem(ANALYTICS_CONSENT_KEY);
      return;
    }
    window.localStorage.setItem(ANALYTICS_CONSENT_KEY, nextState);
  } catch {}
};

export const hasAnalyticsConsent = () =>
  getAnalyticsConsentState() === ANALYTICS_CONSENT_GRANTED;

export const ensureClarityLoaded = (pathname = "/") => {
  if (!isTrackingHostAllowed()) return null;
  if (isClaritySensitivePath(pathname)) return null;

  const clarity = ensureClarityQueue();
  if (!clarity) return null;
  if (typeof document === "undefined") return clarity;
  if (document.getElementById(CLARITY_SCRIPT_ID)) return clarity;

  const script = document.createElement("script");
  script.id = CLARITY_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
  document.head.appendChild(script);
  return clarity;
};

export const ensureMetaPixelLoaded = () => {
  if (!isTrackingHostAllowed()) return null;
  if (!hasAnalyticsConsent()) return null;

  const fbq = ensureMetaPixelQueue();
  if (!fbq) return null;

  if (!window.__lukkeyMetaPixelInitialized) {
    fbq("init", META_PIXEL_ID);
    window.__lukkeyMetaPixelInitialized = true;
  }
  updateMetaPixelConsent(ANALYTICS_CONSENT_GRANTED);

  if (typeof document === "undefined") return fbq;
  if (document.getElementById(META_PIXEL_SCRIPT_ID)) return fbq;

  const script = document.createElement("script");
  script.id = META_PIXEL_SCRIPT_ID;
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);
  return fbq;
};

export const trackMetaPixelPageView = () => {
  if (typeof window === "undefined") return;
  const fbq = ensureMetaPixelLoaded();
  if (typeof fbq !== "function") return;
  fbq("track", "PageView");
};

export const syncAnalyticsIntegrations = ({
  pathname = "/",
  consentState = getAnalyticsConsentState(),
} = {}) => {
  if (!isTrackingHostAllowed()) return;

  const isSensitive = isClaritySensitivePath(pathname);
  if (isSensitive) {
    setClarityDisabled(true);
    updateGoogleConsent(ANALYTICS_CONSENT_DENIED);
    updateMetaPixelConsent(ANALYTICS_CONSENT_DENIED);
    clearClarityCookies();
    return;
  }

  if (consentState === ANALYTICS_CONSENT_GRANTED) {
    setClarityDisabled(false);
    updateGoogleConsent(ANALYTICS_CONSENT_GRANTED);
    ensureClarityLoaded(pathname);
    ensureMetaPixelLoaded();
    return;
  }

  setClarityDisabled(false);
  updateGoogleConsent(ANALYTICS_CONSENT_DENIED);
  updateMetaPixelConsent(ANALYTICS_CONSENT_DENIED);
  clearClarityCookies();
  ensureClarityLoaded(pathname);
};

export const GOOGLE_TRACKING_ID = GOOGLE_MEASUREMENT_ID;
